/**
 * Observation detectors. Pure functions over (signals, intents).
 * Never recommend anything — only describe what's true.
 */

import type {
  CustomerIntent,
  EvidenceSignal,
  Observation,
  OpportunityHorizon,
  VisibilityPresenceDetail,
} from "./types";

const DETECTOR = "observation-detectors@0.1.0";

const DEFICIT: Record<VisibilityPresenceDetail["presence"], number> = {
  absent: 1.0,
  thin: 0.6,
  present: 0.2,
  featured: 0.0,
};

function horizonForSlope(slope: number): OpportunityHorizon {
  if (slope >= 0.6) return "this_week";
  if (slope >= 0.2) return "this_season";
  return "this_year";
}

/**
 * Match a CustomerIntent against visibility signals by shared stem/tag.
 * Returns the highest-presence match (featured > present > thin > absent).
 */
function findPresence(
  intent: CustomerIntent,
  visibility: EvidenceSignal<VisibilityPresenceDetail>[],
): { signal: EvidenceSignal<VisibilityPresenceDetail>; presence: VisibilityPresenceDetail["presence"] } | undefined {
  const intentTags = new Set(intent.tags ?? []);
  let best: { signal: EvidenceSignal<VisibilityPresenceDetail>; presence: VisibilityPresenceDetail["presence"] } | undefined;
  const rank: Record<VisibilityPresenceDetail["presence"], number> = {
    absent: 0,
    thin: 1,
    present: 2,
    featured: 3,
  };
  for (const v of visibility) {
    const subjectTokens = v.subject
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .map((t) => t.replace(/(ies|es|s|ing|ed)$/g, ""))
      .filter((t) => t.length > 3);
    const overlap = subjectTokens.some((t) => intentTags.has(t));
    if (!overlap) continue;
    const presence = v.detail.presence;
    if (!best || rank[presence] > rank[best.presence]) {
      best = { signal: v, presence };
    }
  }
  return best;
}

export function detectObservations(
  signals: EvidenceSignal[],
  intents: CustomerIntent[],
): Observation[] {
  const visibility = signals.filter(
    (s) => s.kind === "visibility.site_presence",
  ) as EvidenceSignal<VisibilityPresenceDetail>[];

  const now = new Date().toISOString();
  const out: Observation[] = [];

  for (const intent of intents) {
    const match = findPresence(intent, visibility);
    const presence = match?.presence ?? "absent";
    const visibility_deficit = DEFICIT[presence];
    const evidence = [...intent.evidence, ...(match ? [match.signal.signal_id] : [])];

    // DemandVisibilityGap — the workhorse observation
    if (visibility_deficit > 0 && intent.demand_strength > 0) {
      out.push({
        observation_id: `obs:gap:${intent.intent_id}`,
        kind: "demand_visibility_gap",
        intent_id: intent.intent_id,
        evidence,
        detected_by: DETECTOR,
        detected_at: now,
        demand_strength: intent.demand_strength,
        visibility_deficit,
        gap_magnitude: intent.demand_strength * visibility_deficit,
        presence,
      });
    }

    // RisingIntent — surfaces urgency
    if (intent.trend === "rising") {
      const slope = 0.4 + intent.demand_strength * 0.6;
      out.push({
        observation_id: `obs:rising:${intent.intent_id}`,
        kind: "rising_intent",
        intent_id: intent.intent_id,
        evidence: intent.evidence,
        detected_by: DETECTOR,
        detected_at: now,
        slope,
        horizon_hint: horizonForSlope(slope),
      });
    }

    // OverInvestedForDemand — visibility invested where demand is weak
    if (presence === "featured" && intent.demand_strength < 0.35) {
      out.push({
        observation_id: `obs:over:${intent.intent_id}`,
        kind: "over_invested_for_demand",
        intent_id: intent.intent_id,
        evidence,
        detected_by: DETECTOR,
        detected_at: now,
        demand_strength: intent.demand_strength,
        presence,
      });
    }
  }

  return out;
}